import { Box, Button, Grid } from "@material-ui/core";
import MailIcon from "@material-ui/icons/Mail";
import { useHistory } from "react-router-dom";
import * as yup from "yup";
import { AdviceDelivery } from "../../api";
import { Client, SendApplicationToClientResult, SendMethod } from "../../common/types";
import { useAppDispatch, useAppSelector } from "../../store";
import { saveClient } from "../../store/adviser";
import FormikWrapper from "../containers/FormikWrapper";
import { Colors, DialogBox, SectionTitleText, SubtitleText } from "../controls/helpers";
import { Checkbox, RadioList, TextAreaInput } from "../controls/inputs";
import EditPreviewDisclosure from "./EditPreviewDisclosure";

const SendApplicationToClient = ({
  open,
  hasMobile,
  isRetention,
  isRetentionAdmin,
  cancel,
  ok,
}: {
  open: boolean;
  hasMobile?: boolean;
  isRetention?: boolean;
  isRetentionAdmin?: boolean;
  cancel: () => void;
  ok: (result: SendApplicationToClientResult, values?: Client) => void;
}) => {
  const { adviserDetails, client, disclosureTemplate, isLoading, isSubmitting } = useAppSelector((state) => state.adviser);
  const history = useHistory();
  const dispatch = useAppDispatch();

  const goToDisclosureTemplate = () => {
    dispatch(saveClient({ ...client, useNZFundsDisclosureTemplate: true }));
    cancel();
    history.push("/adviser/disclosure-template");
  };

  return (
    <DialogBox open={open} icon={<MailIcon htmlColor={Colors.Green} />} isSubmitting={isLoading || isSubmitting}>
      <FormikWrapper
        initialValues={{
          consented: false,
          adviceDelivery: AdviceDelivery.None,
          sendMethod: SendMethod.Email,
          useNZFundsDisclosureTemplate: false,
          ...client,
          smsMessage: client?.smsMessage || (isRetention ? `Hi ${client?.firstName}, here is the NZ Funds KiwiSaver Scheme application. Thanks, ${adviserDetails?.adviserName}.` : ""),
        }}
        validationSchema={yup
          .object({
            consented: yup.boolean(),
            sendMethod: yup.mixed<SendMethod>().required("An invitation method is required").notOneOf([SendMethod.None], "An invitation method is required"),
            useNZFundsDisclosureTemplate: yup.boolean(),
          })
          .test("Adviser confirmation required", "Adviser confirmation required", function (values) {
            return values.consented === true || this?.createError({ path: "consented", message: "Confirmation is required" });
          })
          .test("Has Disclosure Template", "Disclosure template is not set up", function (values) {
            return (
              values.useNZFundsDisclosureTemplate === false || !!disclosureTemplate || this?.createError({ path: `useNZFundsDisclosureTemplate`, message: "Disclosure template required - edit below" })
            );
          })}
        onSubmit={(values) =>
          ok(
            {
              sendEmail: values.sendMethod === SendMethod.Email || values.sendMethod === SendMethod.EmailAndSms,
              sendSms: values.sendMethod === SendMethod.Sms || values.sendMethod === SendMethod.EmailAndSms,
              useNZFundsDisclosureTemplate: values.useNZFundsDisclosureTemplate,
              adviceDelivery: values.adviceDelivery,
            },
            values
          )
        }
      >
        {({ setFieldValue, values }) => (
          <>
            <Grid container spacing={2}>
              <Grid item xs={12}>
                <SectionTitleText hideBorderBottom hideMarginBottom hideMarginTop>
                  Send the application to client
                </SectionTitleText>
              </Grid>
              {!isRetention && <EditPreviewDisclosure onEditClick={goToDisclosureTemplate} />}
              {hasMobile && (
                <>
                  <Grid item xs={12}>
                    <RadioList
                      horizontal
                      labelPlacement="end"
                      name="sendMethod"
                      label="How would you like to send this invite?"
                      options={[
                        { value: SendMethod.Email, label: "Email" },
                        ...(values.useNZFundsDisclosureTemplate ? [] : [{ value: SendMethod.Sms, label: "Mobile" }]),
                        { value: SendMethod.EmailAndSms, label: values.useNZFundsDisclosureTemplate ? "Email & mobile" : "Both" },
                      ]}
                    />
                  </Grid>
                  {isRetention && (values.sendMethod === SendMethod.Sms || values.sendMethod === SendMethod.EmailAndSms) && (
                    <Grid item xs={12}>
                      <TextAreaInput
                        name="smsMessage"
                        minRows={2}
                        maxLength={105}
                        label={
                          <Box component="span" width="100%" display="flex" justifyContent="space-between" alignItems="flex-end">
                            <span>SMS message</span>
                            <span style={{ fontSize: "0.8rem" }}>{values.smsMessage?.length || 0}/105</span>
                          </Box>
                        }
                      />
                    </Grid>
                  )}
                </>
              )}
              <Grid item xs={12}>
                <SubtitleText hideMarginBottom>
                  <Checkbox
                    name="consented"
                    label={
                      <SubtitleText black hideMarginBottom>
                        I confirm that:
                      </SubtitleText>
                    }
                  />
                  <ul style={{ marginTop: 0 }}>
                    {(!isRetention || !isRetentionAdmin) && (
                      <>
                        <li>I have provided the client with required disclosures.</li>
                        <li>I have the client's consent to pre-populate their personal details.</li>
                        <li>If applicable, I have provided an explanation of the risks of transferring between schemes/funds.</li>
                      </>
                    )}
                    <li>I have no reason to believe the client is vulnerable or incapable of assessing the risk of investing.</li>
                  </ul>
                </SubtitleText>
              </Grid>
            </Grid>
            <Box pt={3}>
              <Grid container justifyContent="space-between" spacing={2}>
                <Grid item>
                  <Button color="default" variant="contained" type="button" onClick={cancel}>
                    Cancel
                  </Button>
                </Grid>
                <Grid item>&emsp;&emsp;</Grid>
                <Grid item>
                  {!isRetention ? (
                    <Button color="primary" variant="contained" type="submit">
                      Send to client
                    </Button>
                  ) : (
                    <Grid container spacing={2}>
                      <Grid item>
                        <Button color="primary" variant="contained" type="submit" onClick={() => setFieldValue("adviceDelivery", AdviceDelivery.None)}>
                          Client not called
                        </Button>
                      </Grid>
                      <Grid item>
                        <Button color="primary" variant="contained" type="submit" onClick={() => setFieldValue("adviceDelivery", AdviceDelivery.Phone)}>
                          Client called
                        </Button>
                      </Grid>
                    </Grid>
                  )}
                </Grid>
              </Grid>
            </Box>
          </>
        )}
      </FormikWrapper>
    </DialogBox>
  );
};

export default SendApplicationToClient;
